import {
  checkFiscalNumberControlChar,
  fiscalNumberRegex,
} from '@framework/validation/utils/checkFiscalNumber.ts';
import { normalizeSpecialChars } from '@framework/validation/utils/normalizeSpecialChars.ts';
import { remapOmocodeDigit } from '@framework/validation/utils/remapOmocodeDigit.ts';
import dayjs from 'dayjs';

/**
 * Funzione che valida un codice fiscale italiano in base ai dati forniti.
 * Rif: https://www.studioaleo.it/struttura-codice-fiscale.html
 */
export function checkFiscalCode(
  fiscalCode?: string,
  name?: string,
  surname?: string,
  birthday?: string,
  birthplaceCode?: string,
  sex?: string,
) {
  // Se un qualunque campo non è compilato, non posso validare il CF
  if (
    fiscalCode === undefined ||
    name === undefined ||
    surname === undefined ||
    birthday === undefined ||
    birthplaceCode === undefined ||
    sex === undefined
  ) {
    return true;
  }

  const remappedFiscalCode = normalizeSpecialChars(
    remapOmocodeDigit(fiscalCode.toUpperCase()),
  ); // Rimuovo le modifiche fatte per l'omocodice e lo trasformo in maiuscolo

  // Check validità CF
  if (!fiscalNumberRegex.test(remappedFiscalCode)) {
    // biome-ignore format: <tbd>
    console.log("fiscalNumberRegex.test(remappedFiscalCode) failed", { remappedFiscalCode });
    return false;
  }

  if (!checkFiscalNumberControlChar(fiscalCode)) {
    // biome-ignore format: <tbd>
    console.log("checkFiscalNumberControlChar(fiscalCode) failed", { fiscalCode });
    return false;
  }

  // Recupero dei dati dal CF
  const cfSurname = remappedFiscalCode.slice(0, 3);
  const cfName = remappedFiscalCode.slice(3, 6);
  const cfBirthdate = remappedFiscalCode.slice(6, 11);
  const cfBirthplace = remappedFiscalCode.slice(11, 15);
  const cfSex = Number(cfBirthdate.slice(3, 5)) > 40 ? 'F' : 'M';

  // Confronto dei dati
  if (cfName !== getNameCodeFromString(name)) {
    // biome-ignore format: <tbd>
    console.log("cfName !== getNameCodeFromString(name)", { cfName, getNameCodeFromString: getNameCodeFromString(name) });
    return false;
  }

  if (cfSurname !== getCodeFromString(surname)) {
    // biome-ignore format: <tbd>
    console.log("cfSurname !== surname", { cfSurname, getCodeFromString: getCodeFromString(surname) });
    return false;
  }

  if (cfSex !== sex) {
    console.log('cfSex !== sex', { cfSex, sex });
    return false;
  }

  if (cfBirthdate !== getDateCode(birthday, sex)) return false;

  if (cfBirthplace !== birthplaceCode) return false;
  console.log('[checkFiscalCode] passed', { fiscalCode, remappedFiscalCode });
  return true;
}

/**
 * Estrae la sigla dal nome  seguento le regole del codice fiscale.
 */
function getNameCodeFromString(value: string) {
  const normalized = normalizeSpecialChars(value);
  const consonants = normalized.replace(/[AEIOU]/g, '');

  if (consonants.length >= 4) {
    return consonants[0] + consonants[2] + consonants[3]; // La prima, la **terza** e la **quarta** consonante [1, 3, 4]
  }

  // Fallback: se non ci sono abbastanza consonanti, prendo le vocali, ecc.
  // Stesse regole del cognome
  return getCodeFromString(value);
}

/**
 * Estrae la sigla dal cognome seguento le regole del codice fiscale.
 */
function getCodeFromString(value: string) {
  // rimuovo tutti i caratteri non alfabetici
  const normalized = normalizeSpecialChars(value);

  // Prendo solo le consonanti
  const consonants = normalized.replace(/[AEIOU]/g, '');
  if (consonants.length >= 3) {
    return consonants.slice(0, 3);
  }
  // Se non ci sono abbastanza consonanti, prendo le vocali
  const vowels = normalized.replace(/[BCDFGHJKLMNPQRSTVWXYZ]/g, '');
  const composed = `${consonants}${vowels}`;
  if (composed.length >= 3) {
    return composed.slice(0, 3);
  }
  // Se non ci sono abbastanza caratteri, faccio padding con X
  return composed.padEnd(3, 'X');
}

/**
 * Trasforma una data in un codice da usare nel codice fiscale. (YYMDD)
 * Accetta qualsiasi formato data parsabile da dayjs (YYYY-MM-DD, ISO 8601, datetime, ecc.)
 */
function getDateCode(date: string, sex: string) {
  const parsed = dayjs(date, [
    'DD-MM-YYYY',
    'DD/MM/YYYY',
    'MMM DD YYYY hh:mmA',
    'YYYY-MM-DD',
    'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
  ]);

  if (!parsed.isValid()) return '';

  const year = parsed.format('YY');
  const month = parsed.month();
  const day = parsed.date();

  const monthCode = 'ABCDEHLMPRST'[month];
  const parsedDay = (day + (sex === 'F' ? 40 : 0)).toString().padStart(2, '0');

  return `${year}${monthCode}${parsedDay}`;
}
